import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useDepartments } from '../hooks/useDepartments.js';

/* ── Animated HR Illustration SVG ── */
const HRIllustration = () => (
  <svg viewBox="0 0 420 380" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', maxWidth: '420px' }}>
    {/* Background circles */}
    <circle cx="210" cy="190" r="155" fill="rgba(255,255,255,0.06)" />
    <circle cx="210" cy="190" r="115" fill="rgba(255,255,255,0.05)" />

    {/* Calendar / Leave Card */}
    <rect x="40" y="60" width="150" height="130" rx="16" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
    <rect x="40" y="60" width="150" height="36" rx="16" fill="rgba(255,255,255,0.25)" />
    <rect x="40" y="80" width="150" height="16" fill="rgba(255,255,255,0.25)" />
    <text x="115" y="84" textAnchor="middle" fill="white" fontSize="11" fontWeight="700" fontFamily="Inter,sans-serif">APRIL 2026</text>
    {[0,1,2,3,4,5,6].map(i => (
      <circle key={i} cx={58 + i * 19} cy={115} r={5} fill={i === 3 ? '#4ade80' : i === 5 ? '#f87171' : 'rgba(255,255,255,0.4)'} />
    ))}
    {[0,1,2,3,4,5,6].map(i => (
      <circle key={i} cx={58 + i * 19} cy={135} r={5} fill={i === 1 ? '#facc15' : 'rgba(255,255,255,0.3)'} />
    ))}
    {[0,1,2,3,4].map(i => (
      <circle key={i} cx={58 + i * 19} cy={155} r={5} fill="rgba(255,255,255,0.3)" />
    ))}
    <rect x="48" y="168" width="64" height="14" rx="7" fill="#4ade80" />
    <text x="80" y="179" textAnchor="middle" fill="#14532d" fontSize="8" fontWeight="700" fontFamily="Inter,sans-serif">✓ APPROVED</text>
    <rect x="118" y="168" width="62" height="14" rx="7" fill="#fca5a5" />
    <text x="149" y="179" textAnchor="middle" fill="#7f1d1d" fontSize="8" fontWeight="700" fontFamily="Inter,sans-serif">✗ REJECTED</text>

    {/* Attendance Card */}
    <rect x="228" y="50" width="152" height="105" rx="16" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
    <text x="304" y="76" textAnchor="middle" fill="white" fontSize="10" fontWeight="600" fontFamily="Inter,sans-serif">Today's Attendance</text>
    <circle cx="304" cy="115" r="30" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="8" />
    <circle cx="304" cy="115" r="30" fill="none" stroke="#4ade80" strokeWidth="8"
      strokeDasharray={`${0.78 * 2 * Math.PI * 30} ${2 * Math.PI * 30}`}
      strokeLinecap="round"
      transform="rotate(-90 304 115)" />
    <text x="304" y="112" textAnchor="middle" fill="white" fontSize="13" fontWeight="700" fontFamily="Inter,sans-serif">78%</text>
    <text x="304" y="124" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="7" fontFamily="Inter,sans-serif">Present</text>

    {/* People / Employee Card */}
    <rect x="56" y="218" width="140" height="110" rx="16" fill="rgba(255,255,255,0.13)" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
    <text x="126" y="243" textAnchor="middle" fill="white" fontSize="10" fontWeight="600" fontFamily="Inter,sans-serif">Team Members</text>
    {['#818cf8','#34d399','#fb923c','#f472b6','#60a5fa'].map((c, i) => (
      <g key={i}>
        <circle cx={82 + i * 20} cy={272} r={16} fill={c} stroke="rgba(30,30,80,0.5)" strokeWidth="2" />
        <text x={82 + i * 20} y={277} textAnchor="middle" fill="white" fontSize="9" fontWeight="700" fontFamily="Inter,sans-serif">
          {['P','A','K','R','M'][i]}
        </text>
      </g>
    ))}
    <text x="126" y="305" textAnchor="middle" fill="rgba(255,255,255,0.75)" fontSize="9" fontFamily="Inter,sans-serif">24 Active Employees</text>
    <rect x="68" y="312" width="40" height="10" rx="5" fill="rgba(74,222,128,0.3)" />
    <text x="88" y="320" textAnchor="middle" fill="#4ade80" fontSize="7" fontWeight="600" fontFamily="Inter,sans-serif">18 In</text>
    <rect x="118" y="312" width="40" height="10" rx="5" fill="rgba(248,113,113,0.3)" />
    <text x="138" y="320" textAnchor="middle" fill="#f87171" fontSize="7" fontWeight="600" fontFamily="Inter,sans-serif">4 Leave</text>

    {/* Notification / Alert Card */}
    <rect x="228" y="180" width="152" height="80" rx="16" fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.22)" strokeWidth="1.5" />
    <text x="249" y="205" fill="rgba(255,255,255,0.7)" fontSize="9" fontFamily="Inter,sans-serif">🔔  Leave Request</text>
    <rect x="249" y="212" width="118" height="6" rx="3" fill="rgba(255,255,255,0.2)" />
    <rect x="249" y="222" width="80" height="6" rx="3" fill="rgba(255,255,255,0.15)" />
    <rect x="249" y="238" width="55" height="14" rx="7" fill="rgba(99,179,243,0.35)" />
    <text x="277" y="248" textAnchor="middle" fill="#bfdbfe" fontSize="8" fontWeight="600" fontFamily="Inter,sans-serif">PENDING</text>

    {/* Floating check-in badge */}
    <g style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))' }}>
      <rect x="228" y="275" width="152" height="55" rx="14" fill="rgba(255,255,255,0.18)" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
      <circle cx="254" cy="303" r="12" fill="#4ade80" />
      <text x="254" y="307" textAnchor="middle" fill="white" fontSize="11" fontWeight="700" fontFamily="Inter,sans-serif">✓</text>
      <text x="272" y="300" fill="white" fontSize="9" fontWeight="600" fontFamily="Inter,sans-serif">Check-in Recorded</text>
      <text x="272" y="312" fill="rgba(255,255,255,0.6)" fontSize="8" fontFamily="Inter,sans-serif">09:02 AM · On Time</text>
    </g>

    <circle cx="195" cy="50" r="4" fill="rgba(255,255,255,0.4)" />
    <circle cx="215" cy="42" r="2.5" fill="rgba(255,255,255,0.25)" />
    <circle cx="170" cy="345" r="5" fill="rgba(255,255,255,0.2)" />
    <circle cx="390" cy="160" r="6" fill="rgba(255,255,255,0.15)" />
    <circle cx="30" cy="330" r="4" fill="rgba(255,255,255,0.2)" />
  </svg>
);

const DEPT_PREF_KEY = 'hr_login_selected_department';

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const Login = () => {
  const { loginWithGoogle, completeRegistration, currentUser, googleLoginHint, redirectLoginError, clearRedirectLoginError } = useAuth();
  const { departments } = useDepartments();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDeptForm, setShowDeptForm] = useState(false);
  const [dept, setDept] = useState('');

  useEffect(() => {
    const r = currentUser?.role;
    if (r === 'HR' || r === 'EMPLOYEE' || r === 'PENDING') {
      try {
        sessionStorage.removeItem(DEPT_PREF_KEY);
      } catch {
        /* */
      }
    }
  }, [currentUser?.role]);

  useEffect(() => {
    if (currentUser?.role === 'PENDING' && currentUser?.status === 'REJECTED') {
      setShowDeptForm(true);
      if (currentUser.department) setDept(currentUser.department);
      setError('Your previous request was rejected. Choose a department and submit again.');
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser?.role === 'NEED_DEPT') {
      setShowDeptForm(true);
      setError((prev) => prev || 'Please select your department to request access.');
    }
  }, [currentUser]);

  // After Google redirect on iPhone/Safari, same flow as NEED_DEPT from popup
  useEffect(() => {
    if (googleLoginHint === 'NEED_DEPT') {
      setShowDeptForm(true);
      setError('Please select your department to request access.');
    }
  }, [googleLoginHint]);

  useEffect(() => {
    if (redirectLoginError) {
      setError(redirectLoginError);
      clearRedirectLoginError();
    }
  }, [redirectLoginError, clearRedirectLoginError]);

  const handleLogin = async () => {
    if (typeof window !== 'undefined' && !window.isSecureContext && !/^localhost$/i.test(window.location.hostname)) {
      setError('Open this site with HTTPS (padlock in the address bar). Google sign-in does not work on insecure http:// on phones.');
      return;
    }
    let redirectFlow = false;
    try {
      setError('');
      setLoading(true);
      const result = await loginWithGoogle();
      if (result === null) {
        // signInWithRedirect — keep loading until the browser navigates away (do not unlock button early)
        redirectFlow = true;
        return;
      }
    } catch (err) {
      console.error(err);
      if (err.message === 'Access Denied') {
        setError('Access Denied: Your email is not authorized.');
      } else if (err.message === 'Approval Pending') {
        setError('Waiting for HR Approval. You will be notified once approved.');
      } else if (err.message === 'Request Sent') {
        setError('Request sent for approval. Please wait for HR to approve.');
      } else if (err.message === 'Rejected') {
        setError('Your access request was rejected by HR.');
      } else if (err.message === 'NEED_DEPT') {
        setShowDeptForm(true);
        setError('Please select your department to request access.');
      } else {
        setError(`Failed to log in: ${err.message}`);
      }
    } finally {
      if (!redirectFlow) setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        .login-root {
          display: flex;
          height: 100vh;
          width: 100vw;
          overflow: hidden;
          font-family: 'Inter', sans-serif;
          background: #f0f2fb;
        }

        .login-left {
          flex: 1;
          background: linear-gradient(145deg, #3730a3 0%, #4f46e5 45%, #6d28d9 100%);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 44px 48px;
          position: relative;
          overflow: hidden;
        }

        .login-left::before {
          content: '';
          position: absolute;
          top: -80px;
          right: -80px;
          width: 320px;
          height: 320px;
          border-radius: 50%;
          background: rgba(255,255,255,0.06);
          pointer-events: none;
        }

        .login-left::after {
          content: '';
          position: absolute;
          bottom: -100px;
          left: -60px;
          width: 280px;
          height: 280px;
          border-radius: 50%;
          background: rgba(255,255,255,0.04);
          pointer-events: none;
        }

        .login-brand {
          display: flex;
          align-items: center;
          gap: 12px;
          z-index: 1;
        }

        .login-brand-mark {
          width: 40px;
          height: 40px;
          background: rgba(255,255,255,0.2);
          border: 1.5px solid rgba(255,255,255,0.35);
          border-radius: 11px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 17px;
          font-weight: 800;
          color: white;
          backdrop-filter: blur(4px);
        }

        .login-brand-name {
          font-size: 20px;
          font-weight: 800;
          color: white;
          letter-spacing: -0.5px;
        }

        .login-brand-name span {
          color: rgba(255,255,255,0.6);
        }

        .login-illustration {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px 0;
          z-index: 1;
          animation: floatUp 0.8s ease forwards;
        }

        @keyframes floatUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .login-left-footer {
          z-index: 1;
        }

        .login-tagline-hero {
          font-size: 26px;
          font-weight: 800;
          color: white;
          line-height: 1.3;
          letter-spacing: -0.5px;
          margin-bottom: 10px;
        }

        .login-tagline-hero span {
          color: rgba(255,255,255,0.55);
        }

        .login-hero-sub {
          font-size: 13px;
          color: rgba(255,255,255,0.55);
          line-height: 1.6;
          max-width: 360px;
        }

        .login-stats-row {
          display: flex;
          gap: 18px;
          margin-top: 24px;
        }

        .login-stat {
          background: rgba(255,255,255,0.1);
          border: 1px solid rgba(255,255,255,0.18);
          border-radius: 12px;
          padding: 12px 18px;
          backdrop-filter: blur(6px);
        }

        .login-stat-num {
          font-size: 20px;
          font-weight: 800;
          color: white;
        }

        .login-stat-lbl {
          font-size: 10px;
          color: rgba(255,255,255,0.55);
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-top: 2px;
        }

        .login-right {
          width: 420px;
          min-width: 420px;
          background: #ffffff;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 48px;
          box-shadow: -12px 0 48px rgba(0,0,0,0.08);
          position: relative;
          overflow-y: auto;
          overflow-x: hidden;
        }

        .login-form-container {
          width: 100%;
          max-width: 320px;
          margin: 0 auto;
        }

        .login-form-top {
          margin-bottom: 36px;
        }

        .login-welcome {
          font-size: 24px;
          font-weight: 800;
          color: #1e1b4b;
          letter-spacing: -0.5px;
          margin-bottom: 6px;
        }

        .login-welcome-sub {
          font-size: 13.5px;
          color: #6b7280;
          line-height: 1.5;
        }

        .login-divider {
          display: flex;
          align-items: center;
          gap: 12px;
          margin: 28px 0;
        }

        .login-divider-line {
          flex: 1;
          height: 1px;
          background: #e5e7eb;
        }

        .login-divider-text {
          font-size: 11px;
          font-weight: 600;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.8px;
        }

        .login-google-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 14px 20px;
          border-radius: 12px;
          border: 1.5px solid #e5e7eb;
          background: white;
          color: #374151;
          font-family: 'Inter', sans-serif;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.18s ease;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
        }

        .login-google-btn:hover:not(:disabled) {
          border-color: #4f46e5;
          background: #fafafa;
          box-shadow: 0 4px 16px rgba(79,70,229,0.12);
          transform: translateY(-1px);
        }

        .login-google-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .login-cta-btn {
          width: 100%;
          padding: 14px 20px;
          border-radius: 12px;
          border: none;
          background: linear-gradient(135deg, #4f46e5, #6d28d9);
          color: white;
          font-family: 'Inter', sans-serif;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.18s ease;
          box-shadow: 0 4px 14px rgba(79,70,229,0.3);
        }

        .login-cta-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(79,70,229,0.4);
        }

        .login-cta-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .login-spinner {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255,255,255,0.35);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
          margin-right: 8px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .login-error {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #b91c1c;
          padding: 12px 14px;
          border-radius: 10px;
          margin-bottom: 20px;
          font-size: 13px;
          font-weight: 500;
          line-height: 1.5;
        }

        .login-dept-label {
          font-size: 12px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 6px;
          display: block;
        }

        .login-dept-select {
          width: 100%;
          padding: 12px 14px;
          border: 1.5px solid #e5e7eb;
          border-radius: 10px;
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          color: #1e1b4b;
          background: white;
          outline: none;
          margin-bottom: 16px;
          transition: border-color 0.15s;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='%236b7280' d='M4.5 6L8 9.5 11.5 6z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 14px center;
          background-size: 16px;
        }

        .login-dept-select:focus {
          border-color: #4f46e5;
          box-shadow: 0 0 0 3px rgba(79,70,229,0.1);
        }

        .login-tagline {
          display: none;
        }

        @media (max-width: 768px) {
          /* iOS/Android: 100vh + overflow:hidden often clips the form below the fold (dynamic browser chrome). */
          .login-root {
            flex-direction: column;
            overflow-x: hidden;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
            min-height: 100vh;
            min-height: 100dvh;
            height: auto;
            max-height: none;
          }
          .login-left {
            flex: none;
            flex-shrink: 0;
            max-height: 40vh;
            height: auto;
            min-height: 100px;
            padding: 20px;
            justify-content: center;
          }
          .login-left-footer, .login-left::before, .login-left::after {
            display: none;
          }
          .login-illustration {
            padding: 0;
            transform: scale(0.85);
            margin-top: 45px;
          }
          .login-brand {
            position: absolute;
            top: 25px;
            left: 20px;
            z-index: 10;
          }
          .login-right {
            flex: 1 1 auto;
            width: 100%;
            min-width: 100%;
            min-height: 0;
            padding: 24px;
            padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
            justify-content: flex-start;
          }
          .login-form-top {
            margin-bottom: 25px;
          }
          .login-welcome {
            font-size: 26px;
            margin-bottom: 8px;
          }
          .login-hero-sub {
            font-size: 15px;
            max-width: 280px;
            margin: 0 auto;
          }
          .login-divider, .login-features, .login-footer-note {
            display: none; 
          }
          .login-tagline {
            display: block;
            text-align: center;
            font-size: 13px;
            color: #6b7280;
            margin-top: 16px;
            font-style: italic;
            max-width: 240px;
            margin-left: auto;
            margin-right: auto;
          }
          .login-google-btn {
            height: 54px;
            font-size: 16px;
          }
        }
      `}</style>

      <div className="login-root">
        <div className="login-left">
          <div className="login-brand">
            <div className="login-brand-mark">A</div>
            <div className="login-brand-name">Acad<span>XL</span> HR</div>
          </div>

          <div className="login-illustration">
            <HRIllustration />
          </div>

          <div className="login-left-footer">
            <h2 className="login-tagline-hero">Seamless HR Management <span>Powered by AI.</span></h2>
            <p className="login-hero-sub">Manage attendance, leaves, and team productivity in seconds.</p>
            
            <div className="login-stats-row">
              <div className="login-stat">
                <div className="login-stat-num">5k+</div>
                <div className="login-stat-lbl">Active Logins</div>
              </div>
              <div className="login-stat">
                <div className="login-stat-num">98%</div>
                <div className="login-stat-lbl">Accuracy</div>
              </div>
            </div>
          </div>
        </div>

        <div className="login-right">
          <div className="login-form-container">
            <div className="login-form-top">
              <h1 className="login-welcome">Welcome back!</h1>
              <p className="login-welcome-sub">
                {showDeptForm
                  ? 'Complete your access request below.'
                  : 'Sign in with Google to continue.'}
              </p>
            </div>

            {error && (
              <div className="login-error">
                <span style={{ fontSize: '18px' }}>⚠️</span>
                {error}
              </div>
            )}

            {showDeptForm ? (
              <div style={{ animation: 'floatUp 0.4s easeOut' }}>
                <h3 className="login-dept-heading">Almost there!</h3>
                <p className="login-dept-sub">Select your department to request access.</p>
                
                <label className="login-dept-label">DEPARTMENT</label>
                <select 
                  className="login-dept-select"
                  value={dept} 
                  onChange={(e) => setDept(e.target.value)}
                >
                  <option value="">Choose department...</option>
                  {departments.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>

                <button 
                  type="button"
                  onClick={async () => {
                    if (!dept) return;
                    setLoading(true);
                    try {
                      await completeRegistration(dept);
                      try {
                        sessionStorage.removeItem(DEPT_PREF_KEY);
                      } catch {
                        /* */
                      }
                      setShowDeptForm(false);
                      setError('');
                      // Error will be cleared by the real-time PENDING role transition
                    } catch(e) {
                      setError("Failed to register.");
                    }
                    setLoading(false);
                  }}
                  disabled={loading || !dept}
                  className="login-cta-btn"
                >
                  {loading && <span className="login-spinner"></span>}
                  Submit Access Request
                </button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleLogin}
                  disabled={loading}
                  className="login-google-btn"
                  style={{ marginBottom: 18 }}
                >
                  {loading ? <span className="login-spinner"></span> : <GoogleIcon />}
                  {loading ? 'Opening Google…' : 'Sign in with Google'}
                </button>

                <p className="login-tagline">Your career journey, simplified and managed.</p>

                <div className="login-divider">
                  <div className="login-divider-line"></div>
                  <span className="login-divider-text">Authorized Access Only</span>
                  <div className="login-divider-line"></div>
                </div>

                <div className="login-footer-note">
                  Sign in with your AcadXL Google account.<br/>
                  For staff and faculty only — keep your login private and do not share access.
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
